/* eslint-disable func-names */
/* global AblyPageData */

/*
 *
 * Global dependencies (closure scope arguments)
 *
 * "NavLangs" is injected by Ruby as an inline script tag
 * see: /app/helpers/documentation_helper.rb:
 *
 * "CodeWindow" is initialized by:
 * /app/assets/javascripts/modules/code_window.js.coffee
 *
 * "Helpers" is initialized by:
 * /app/assets/javascripts/modules/documentation/helpers.js
 *
 */

import getSupportedLanguages from './getSupportedLanguages';
import getPermittedLanguages from './getPermittedLanguages';
import getFriendlyLanguage from './getFriendlyLanguage';
import languageAvailableOnPage from './languageAvailableOnPage';
import setLanguageHintVisibility from './setLanguageHintVisibility';
import setupLanguageNavigation from './setupLanguageNavigation';
import renderDocumentationNavigation from './renderDocumentationNavigation';
import setupPermittedLanguages from './setupPermittedLanguages';
import groupLanguageSpecificContent from './groupLanguageSpecificContent';
import wrapAndGroupCodeBlocks from './wrapAndGroupCodeBlocks';
import htmlTemplates from './htmlTemplates';

((global, $, erb) => {
  // External dependencies
  const code = global.CodeWindow;
  const helpers = global.Helpers;

  const methods = {
    body: $('body.page-documentation'),
    html: htmlTemplates,
    helpers,
    code,

    // Use internal Proxy to break global reference
    permittedLanguageKeys: getPermittedLanguages(erb),
    supportedLanguages: getSupportedLanguages(erb),
    navigationLanguages: [...(global.NavLangs || [])],

    //  External references:
    //  javascripts/page/documentation.js.coffee
    //  - DocLang.setupPermittedLanguages()
    //  - DocLang.setupLanguageNavigationMenu()
    //  - DocLang.groupLanguageSpecificContent($body)
    //  - DocLang.wrapAndGroupCodeBlocks($body)
    //  - DocLang.languageAvailableOnPage(language)

    wrapAndGroupCodeBlocks,
    languageAvailableOnPage,
    groupLanguageSpecificContent,
    setupPermittedLanguages,
    setupLanguageNavigationMenu() {
      const { permittedLanguageKeys } = this;
      const [jumps, languages] = setupLanguageNavigation(permittedLanguageKeys);
      renderDocumentationNavigation(jumps, languages);
    },

    getCurrentLanguage() {
      return this.helpers.getCurrentLanguage();
    },

    activateButton(button) {
      return this.helpers.activateButton(button);
    },

    permittedLanguages(array = null) {
      // Get or set the global language Array
      if (array && array.constructor === Array) this.permittedLanguageKeys = [...array];
      return this.permittedLanguageKeys || [];
    },

    // External reference:
    // ./app/assets/javascripts/modules/documentation/code_menu.js.coffee
    showLanguageSpecificContentAndCode() {
      const language = this.getCurrentLanguage();
      const langBlocks = this.showCodeGroupsForCurrentLanguage(language);

      this.showContentBlocksForCurrentLanguage(language);

      const body = document.querySelector('body');
      body.classList.remove('not-rendered');
      body.dispatchEvent(new CustomEvent('rendered'));

      this.hideTitlesFromEmptyContentGroups();
      this.code.addSyntaxHighlightingToCodeBlocks();
      this.code.addSyntaxHighlightingToBlockQuoteDefinition();
      this.updateLanguageSpecificLinks(language);

      Array.from(langBlocks)
        .filter((el) => !/none/i.test(el.style.display || ''))
        .forEach(() => setLanguageHintVisibility(this));
    },

    // Any contiguous code blocks are assigned a unique
    // data-lang-code-group id
    // This method simply hides any language block in a group
    // that does not match the current language
    showCodeGroupsForCurrentLanguage(language) {
      const dataLangGroupsHandled = {};
      const currentLanguage = this.friendlyCurrentLang();
      const that = this;

      return $('[data-lang-code-group] pre[lang]').each(function () {
        const $this = $(this);
        const $group = $(this).parent();
        const groupId = $group.attr('data-lang-code-group');

        if (dataLangGroupsHandled[groupId]) return;

        dataLangGroupsHandled[groupId] = true;

        const $siblings = $group.find('> pre');
        const $exactMatch = $siblings.filter(function () {
          return $(this).attr('lang').split(',').indexOf(language) > -1;
        });

        if ($exactMatch.length) {
          $siblings.not($exactMatch).hide();
          $exactMatch.show();
          $group.find('.code-window-menu-note').hide();
          $group.find('.code-window-menu').hide();
          return;
        }

        // We need to look for a fallback language
        // Select fallback code example and display it
        const $fallback = $siblings.first();
        const $menu = $this.prev('.code-window-menu');

        // Activate button that matches fallback example
        const $button = $menu.find(`button[lang="${$fallback.attr('lang')}"]`);

        // Add warning of 'No example present' and display toolbar
        // using the current language selected
        $menu.find('span.toolbar-mode').text(currentLanguage);
        $menu.find('.code-window-menu-note').show();
        $menu.show();

        // Simulate lang button being pressed
        that.switchDisplayedBlockButtonEvent($button);
        that.helpers.activateButton($button);
      });
    },

    // Find any node that is part of a lang content group
    // Find all siblings in the content group
    // If any siblings including self have an exact match on the lang then show that node
    // Else show the default node if it exists
    // Ensure all other nodes are hidden in that lang content group

    showContentBlocksForCurrentLanguage(language) {
      const contentGroupsProcessed = {};
      return $('[data-lang-content-group]').each(function () {
        const contentGroupId = $(this).data('lang-content-group');

        if (contentGroupsProcessed[contentGroupId]) return;

        contentGroupsProcessed[contentGroupId] = true;

        const $this = $(this);
        const $siblings = $(`[data-lang-content-group=${contentGroupId}]`);
        const $default = $siblings.filter('[lang=default]');
        const $exactMatch = $siblings.filter(function () {
          return $(this).attr('lang').split(',').indexOf(language) > -1;
        });

        // Definition list row should only be shown
        // if <div lang="lang"> matches lang
        if ($this.is('div') && $this.closest('dl').length) {
          $siblings.each(function () {
            return $(this).closest('.definition-list-row').addClass('hidden');
          });

          if ($exactMatch.length) {
            $exactMatch.closest('.definition-list-row').removeClass('hidden');
          } else {
            $default.closest('.definition-list-row').removeClass('hidden');
          }
          return;
        }

        if ($exactMatch.length) {
          $siblings.not($exactMatch).addClass('hidden');
          $exactMatch.removeClass('hidden');
          return;
        }

        $siblings.not($default).addClass('hidden');
        $default.removeClass('hidden');
      });
    },

    switchDisplayedBlockButtonEvent($button) {
      const mode = $button.attr('lang');
      // this.helpers.activateButton($this);

      $button.parents('[data-lang-code-group]').children('pre[lang]').hide();

      const $langBlock = $button.parents('[data-lang-code-group]').children(`pre[lang=${mode}]`).show();

      // As we don't syntax highlight hidden elements as a rendering optimisation
      // we need to be sure the new code being shown has highlighting
      this.code.addSyntaxHighlightingToCodeBlocks();

      // Ensure the language hints are suitably visible
      setLanguageHintVisibility($langBlock);
    },

    // 1. Finds all heading anchor targets that have lang variations
    // 2. Then gets the text from the anchor target based on which one is visible due to selected lang
    // 3. Then updates the links to that heading anchor target with the text in the visible anchor targe
    //    that are contained within one of the nav blocks.

    updateLanguageSpecificLinks() {
      $(':header > [lang]').each(function () {
        const id = $(this).parent().attr('id');
        const anchor = `#${id}`;
        let langSpecificLinkText = $(`${anchor} > [lang]:visible:first`).text();
        return $('[data-jump-menu]:not([data-jump-menu-no-hidden-links]), [data-toc-menu]')
          .find(`a[href="${anchor}"]:not(.header-link)`)
          .each(function () {
            if (langSpecificLinkText.length === 0) {
              return $(this).hide().addClass('is-hidden-due-specific-lang');
            }

            // break on ::, ., or \ namespace separators
            const parts = langSpecificLinkText.split(/::|\\|\./);
            const splitter = langSpecificLinkText.match(/::|\\|\./g);
            const lastPart = parts[parts.length - 1];

            if (splitter && (lastPart.match(/^[A-Z0-9]+$/) || lastPart.length <= 6)) {
              // If the last part of the definition is a Ruby constant
              // i.e. PresenceMessage::ACTION
              // then show the class to add context.
              // Equally, if the last part is very short
              // i.e. PresenceMessage.Action
              // then it to needs context */

              langSpecificLinkText = parts[parts.length - 2] + splitter[splitter.length - 1] + lastPart;
            } else {
              langSpecificLinkText = lastPart;
            }

            $(this).show().removeClass('is-hidden-due-specific-lang');
            return $(this).text(langSpecificLinkText);
          });
      });

      $('[data-js-docs-article-content] h6').each(function () {
        const id = $(this).attr('id');
        if (!id) return;

        const anchor = `#${id}`;
        const langSpecificLinkText =
          $(`${anchor} > [lang]`).length > 0 ? $(`${anchor} > [lang]:visible`).text() : $(`${anchor}:visible`).text();

        $(`a[href="${anchor}"]:not(.header-link)`).each(function () {
          $(this).text(langSpecificLinkText);
        });
      });
    },

    friendlyLanguage(string) {
      return getFriendlyLanguage.call(this, string);
    },

    friendlyCurrentLang() {
      const currentLanguage = this.getCurrentLanguage();
      return this.friendlyLanguage(currentLanguage);
    },

    hideTitlesFromEmptyContentGroups() {
      const contentGroupsProcessed = {};

      return $('[data-lang-content-group]').each(function () {
        let $this = $(this);
        const $parent = $this.parent('h1, h2, h3, h4, h5, h6');
        const id = $this.data('lang-content-group');

        if (contentGroupsProcessed[id]) {
          return;
        }
        contentGroupsProcessed[id] = true;

        const visibleLanguages = $parent.children('span').filter(function () {
          $this = $(this);
          if ($this.css('display') === 'none') {
            return false;
          }
          return true;
        });

        const method = visibleLanguages.length === 0 ? 'hide' : 'show';
        $parent[method]();
      });
    },

    ensureBlockquoteDefinitionIsShown(langGroupSelector) {
      let isSomeBlockVisible = false;
      $(langGroupSelector).each(function () {
        const $this = $(this);
        if ($this.css('display') === 'inline') {
          isSomeBlockVisible = true;
          $this.parents('blockquote').show();
        }
      });

      if (isSomeBlockVisible) return;

      // If no blockquote definition (aka method definitions) is visible, try to
      // find one set as default and show it.
      const groupSelector = `${langGroupSelector}[lang=default]`;
      const defaultContent = $(groupSelector);
      if (defaultContent.length > 0) {
        defaultContent.show();
        defaultContent.parents('blockquote').show();
      }
    },
  };
  // Append public methods to global scope
  global.DocLang = methods;
  global.LangsPermitted = methods.permittedLanguageKeys;
})(window, jQuery, AblyPageData);
